/**
 * Hook de vérification périodique du token Sanctum (Auth Check)
 * 
 * Le package consulte l'endpoint POST /api/sso/check-token du SaaS
 * pour vérifier si l'utilisateur est toujours connecté.
 * 
 * - Premier check 60s après login
 * - Checks suivants toutes les 2 min
 * - Si status === 'connected' → met à jour user_infos via le storage du package
 * - Si 401 → révoque l'IAM (POST /sso/disconnect) + nettoie le frontend
 * - Ne déconnecte PAS si offline ou serveur inaccessible
 * 
 * @version 2.7.9
 */

import { useEffect, useRef, useCallback } from 'react';
import { getHeaders, getAuthToken, getNativeStorage, STORAGE_KEYS, ensureDeviceContext } from '../services/api';
import type { UserInfos } from '../types/native';

const FIRST_CHECK_DELAY = 60 * 1000;       // 60 secondes
const INTERVAL_DELAY = 2 * 60 * 1000;      // 2 minutes

export interface UseTokenHealthCheckOptions {
  /** Whether to enable health checks (true when authenticated) */
  enabled: boolean;
  /** SaaS API base URL */
  saasApiUrl: string;
  /** IAM API base URL (for revocation on 401) */
  iamApiUrl: string;
  /** Called when the backend explicitly invalidates the token (401) */
  onTokenInvalid: () => void;
  /**
   * Optional handler for 401. If provided, it can attempt a refresh and decide
   * whether the session was recovered.
   *
   * Return values:
   * - 'recovered': session is valid again (do NOT call onTokenInvalid)
   * - 'invalid': token is explicitly invalid (call onTokenInvalid)
   * - 'noop': fall back to default behavior
   */
  onUnauthorized?: () => Promise<'recovered' | 'invalid' | 'noop'>;
  /** Called when fresh user_infos are received */
  onUserUpdated?: (userInfos: UserInfos) => void;
  /** Debug mode */
  debug?: boolean;
}

/**
 * Appelle POST /api/sso/check-token sur le SaaS.
 * 
 * Le SaaS doit retourner :
 * - 200 { status: 'connected', user: { ...user_infos } }  → token valide
 * - 401 { status: 'disconnected', message: '...' }        → token invalide
 */
async function checkTokenValidity(
  saasApiUrl: string,
  token: string,
  debug: boolean
): Promise<{ valid: boolean; user?: UserInfos }> {
  // 1. Check connectivity first
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    if (debug) console.log('🔄 [HealthCheck] Offline — skip');
    throw new Error('offline');
  }

  await ensureDeviceContext();

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

  try {
    const response = await fetch(`${saasApiUrl}/sso/check-token`, {
      method: 'POST',
      headers: getHeaders(token, true),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    // 401 = seul signal explicite de déconnexion (Sanctum ou backend)
    if (response.status === 401) {
      if (debug) console.log('🔄 [HealthCheck] Token invalide (401) — déconnexion');
      return { valid: false };
    }

    // Tout autre code non-OK = problème serveur, PAS un problème de token → session conservée
    if (!response.ok) {
      if (debug) console.log(`🔄 [HealthCheck] Erreur serveur ${response.status} — session conservée`);
      throw new Error(`server_error_${response.status}`);
    }

    const data = await response.json();

    // Format standard : { status: 'connected', user: {...} }
    if (data.status === 'connected' && data.user) {
      if (debug) console.log('🔄 [HealthCheck] Token valide ✅ — user_infos mis à jour');
      return { valid: true, user: data.user };
    }

    // HTTP 200 = Sanctum a confirmé l'authentification, token valide
    // On ne déconnecte JAMAIS sur un body ambigu : seul le 401 fait foi
    if (debug) console.log('🔄 [HealthCheck] Token valide ✅');
    return { valid: true, user: data.user };
  } catch (error) {
    clearTimeout(timeoutId);

    if (error instanceof Error && error.message === 'offline') {
      throw error;
    }

    // Network errors, timeouts, server unreachable → do NOT invalidate
    if (debug) {
      const msg = error instanceof Error ? error.message : 'unknown';
      console.log(`🔄 [HealthCheck] Erreur réseau/serveur (${msg}) — session conservée`);
    }
    throw error;
  }
}

/**
 * Révoque la session sur l'IAM (fire-and-forget).
 * Appelé quand le SaaS retourne 401 pour s'assurer que le token de session
 * IAM est aussi révoqué côté IAM.
 */
function revokeOnIam(iamApiUrl: string, debug: boolean): void {
  try {
    const controller = new AbortController();
    setTimeout(() => controller.abort(), 5000); // 5s timeout

    const iamToken = getAuthToken();
    if (!iamToken) return;

    const payload: Record<string, string> = { iam_token: iamToken };

    if (debug) {
      console.log('🔄 [HealthCheck] Revocation IAM:', { hasRef: true });
    }

    fetch(`${iamApiUrl}/sso/disconnect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify(payload),
      signal: controller.signal,
    }).catch(() => {
          if (debug) console.log('🔄 [HealthCheck] Échec revocation IAM (non-bloquant)');
        });
  } catch {
    // Fire-and-forget — never block
  }
}

export function useTokenHealthCheck(options: UseTokenHealthCheckOptions): void {
  const { enabled, saasApiUrl, iamApiUrl, onTokenInvalid, onUnauthorized, onUserUpdated, debug = false } = options;

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const enabledRef = useRef(enabled);
  const callbacksRef = useRef({ onTokenInvalid, onUserUpdated });

  // Keep refs up to date
  enabledRef.current = enabled;
  callbacksRef.current = { onTokenInvalid, onUserUpdated };

  const performCheck = useCallback(async () => {
    if (!enabledRef.current) return;

    const storage = getNativeStorage();
    const token = storage.getItem(STORAGE_KEYS.AUTH_TOKEN) || storage.getItem(STORAGE_KEYS.TOKEN);

    if (!token) {
      if (debug) console.log('🔄 [HealthCheck] Pas de token — arrêt');
      return;
    }

    try {
      const result = await checkTokenValidity(saasApiUrl, token, debug);

      if (!result.valid) {
        // Token explicitly rejected by SaaS (401)
        // Optional recovery path: try refresh before invalidating locally.
        if (onUnauthorized) {
          try {
            const decision = await onUnauthorized();
            if (decision === 'recovered') {
              if (debug) console.log('🔄 [HealthCheck] Session récupérée via refresh ✅');
              return;
            }
            if (decision === 'noop') {
              // fall through
            } else if (decision === 'invalid') {
              // proceed to invalidation below
            }
          } catch {
            // ignore, proceed to invalidation below
          }
        }

        // 1. Revoke on IAM (fire-and-forget)
        if (iamApiUrl) {
          if (debug) console.log('🔄 [HealthCheck] Revocation IAM avec iam_token...');
          revokeOnIam(iamApiUrl, debug);
        }

        // 2. Clear frontend session
        callbacksRef.current.onTokenInvalid();
        return;
      }

      // Token valid — update user_infos if provided
      if (result.user && callbacksRef.current.onUserUpdated) {
        callbacksRef.current.onUserUpdated(result.user);
      }
    } catch {
      // Network/timeout/server error → silently keep session
    }
  }, [saasApiUrl, iamApiUrl, debug, onUnauthorized]);

  useEffect(() => {
    // Cleanup previous timers
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!enabled || !saasApiUrl) return;

    if (debug) console.log('🔄 [HealthCheck] Activé — premier check dans 60s, puis toutes les 2 min');

    // First check after 60 seconds
    timerRef.current = setTimeout(() => {
      performCheck();

      // Then every 2 minutes
      intervalRef.current = setInterval(performCheck, INTERVAL_DELAY);
    }, FIRST_CHECK_DELAY);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [enabled, saasApiUrl, performCheck, debug]);
}

export default useTokenHealthCheck;
